import { useEffect, useState, useCallback } from "react";
import { Smartphone, Bell, Save, CheckCircle2, XCircle, RefreshCw, QrCode, Hash, KeyRound } from "lucide-react";
import { BRANDING_THEME as BRAND_COLORS } from "../../../constants/brandingTheme";
import ClientService from "../../../services/client/ClientService";
import useWhatsappSocket from "../../../hooks/useWhatsappSocket";
import { showError, showSuccess } from "../../../components/alertas";
import logger from "../../../utils/logger";
import { CountryPhoneSelector } from "../../../components/CountryPhoneSelector";
import { validatePhoneForCountry, normalizePhoneToInternational, detectCountryFromPhone, getPhoneWithoutCountryCode } from "../../../services/countries/phoneNormalizer";

// clientId reservado del número-plataforma (debe coincidir con whatsapp.constants.ts del backend)
const PLATFORM_ID = "platform";

function PlatformSettings() {
  // Número de alertas: se guarda internacional (lada + número); en pantalla va en dos partes.
  const [adminCountry, setAdminCountry] = useState("MX");
  const [adminNumber, setAdminNumber] = useState("");
  const [adminNumberError, setAdminNumberError] = useState("");
  const [platformStatus, setPlatformStatus] = useState(null);
  const [platformNumber, setPlatformNumber] = useState(null);
  const [qrCode, setQrCode] = useState(null);
  const [connecting, setConnecting] = useState(false);
  const [savingNumber, setSavingNumber] = useState(false);
  // Vinculación: "qr" (escanear) o "code" (código de 8 caracteres con el número). Es uno u otro por sesión.
  const [connectMode, setConnectMode] = useState("qr");
  const [phoneInput, setPhoneInput] = useState("");
  const [phoneCountry, setPhoneCountry] = useState("MX");
  const [phoneError, setPhoneError] = useState("");
  const [pairingCode, setPairingCode] = useState(null);
  const [requestingCode, setRequestingCode] = useState(false);

  const isConnected = platformStatus === "authenticated" || platformStatus === "ready";

  useEffect(() => {
    (async () => {
      try {
        const cfg = await ClientService.getSystemConfig();
        const saved = cfg?.superadminWhatsapp || "";
        const country = detectCountryFromPhone(saved) || "MX";
        let local = getPhoneWithoutCountryCode(saved, country);
        // Números MX guardados con el "1" de WhatsApp (521…): se quita para mostrar los 10 dígitos.
        if (country === "MX" && local.length === 11 && local.startsWith("1")) local = local.slice(1);
        setAdminCountry(country);
        setAdminNumber(local);
        setPlatformStatus(cfg?.platformWhatsappStatus || null);
        setPlatformNumber(cfg?.platformWhatsappNumber || null);
      } catch (error) {
        logger.error("Error cargando config de plataforma:", error);
      }
    })();
  }, []);

  const handleQr = useCallback((qr) => {
    setQrCode(qr);
    setConnecting(false);
  }, []);

  const handlePairingCode = useCallback((code) => {
    setPairingCode(code);
    setRequestingCode(false);
  }, []);

  const handleAuthenticated = useCallback(() => {
    setQrCode(null);
    setPairingCode(null);
    setConnecting(false);
    setRequestingCode(false);
    setPlatformStatus("authenticated");
    showSuccess("Plataforma conectada", "El número de plataforma quedó vinculado.");
  }, []);

  const handleSocketError = useCallback((err) => {
    setConnecting(false);
    setRequestingCode(false);
    showError("Error de conexión", err || "No se pudo conectar el número-plataforma.");
  }, []);

  useWhatsappSocket(PLATFORM_ID, handleQr, handleAuthenticated, handleSocketError, handlePairingCode);

  const startConnect = async () => {
    try {
      setConnecting(true);
      setQrCode(null);
      setPairingCode(null);
      await ClientService.getWhatsAppQr(PLATFORM_ID); // dispara el proceso; el QR llega por socket
    } catch (error) {
      setConnecting(false);
      showError("Error", error.response?.data?.message || error.message);
    }
  };

  const requestPairingCode = async () => {
    const check = validatePhoneForCountry(phoneInput || "", phoneCountry);
    if (!check.valid) {
      showError("Número inválido", check.message);
      return;
    }
    try {
      setRequestingCode(true);
      setQrCode(null);
      setPairingCode(null);
      const res = await ClientService.getWhatsAppPairingCode(PLATFORM_ID, normalizePhoneToInternational(phoneInput, phoneCountry)); // el código llega por socket
      if (res && res.success === false) throw new Error(res.message || "No se pudo generar el código");
    } catch (error) {
      setRequestingCode(false);
      showError("Error", error.response?.data?.message || error.message);
    }
  };

  const switchMode = (mode) => {
    if (mode === connectMode) return;
    setConnectMode(mode);
    setQrCode(null);
    setPairingCode(null);
    setConnecting(false);
    setRequestingCode(false);
  };

  const saveAdminNumber = async () => {
    const check = validatePhoneForCountry(adminNumber || "", adminCountry);
    if (!check.valid) {
      showError("Número inválido", check.message);
      return;
    }
    try {
      setSavingNumber(true);
      await ClientService.updateSystemConfig({ superadminWhatsapp: normalizePhoneToInternational(adminNumber, adminCountry) });
      showSuccess("Guardado", "Número de alertas actualizado.");
    } catch (error) {
      showError("Error", error.response?.data?.message || error.message);
    } finally {
      setSavingNumber(false);
    }
  };

  return (
    <div className="d-flex flex-column gap-4">
      {/* Conexión del número-plataforma */}
      <div className="app-card" style={{ backgroundColor: BRAND_COLORS.backgrounds.secondary, borderColor: BRAND_COLORS.borders.light }}>
        <div className="card-body" style={{ padding: "1.25rem" }}>
          <h3 className="h6 d-flex align-items-center gap-2 mb-1" style={{ color: BRAND_COLORS.primary.main, fontSize: "1rem" }}>
            <Smartphone size={18} /> Número-plataforma (OTP y alertas)
          </h3>
          <p className="small mb-3" style={{ color: BRAND_COLORS.text.muted, fontSize: "0.8rem" }}>
            Este número envía los códigos de verificación del registro público y las alertas a tu WhatsApp.
            Conéctalo una sola vez escaneando el QR o con un código de vinculación.
          </p>

          <div className="d-flex align-items-center gap-2 mb-3">
            {isConnected ? (
              <CheckCircle2 size={18} style={{ color: BRAND_COLORS.primary.main }} />
            ) : (
              <XCircle size={18} style={{ color: BRAND_COLORS.error }} />
            )}
            <span style={{ color: BRAND_COLORS.text.secondary, fontSize: "0.85rem" }}>
              {isConnected ? "Conectado" : "No conectado"}
              {platformNumber ? ` · ${platformNumber}` : ""}
              {platformStatus && !isConnected ? ` (${platformStatus})` : ""}
            </span>
          </div>

          <div className="sa-segment mb-3" role="tablist" aria-label="Método de vinculación">
            <button type="button" role="tab" aria-selected={connectMode === "qr"} className={`sa-segment-btn ${connectMode === "qr" ? "is-active" : ""}`} onClick={() => switchMode("qr")}>
              <QrCode size={14} style={{ verticalAlign: "-2px", marginRight: 4 }} /> Escanear QR
            </button>
            <button type="button" role="tab" aria-selected={connectMode === "code"} className={`sa-segment-btn ${connectMode === "code" ? "is-active" : ""}`} onClick={() => switchMode("code")}>
              <Hash size={14} style={{ verticalAlign: "-2px", marginRight: 4 }} /> Usar código
            </button>
          </div>

          {connectMode === "qr" ? (
            qrCode ? (
              <div className="text-center">
                <img src={qrCode} alt="QR de plataforma" style={{ maxWidth: 260, width: "100%", borderRadius: 8 }} />
                <p className="small mt-2" style={{ color: BRAND_COLORS.text.muted, fontSize: "0.75rem" }}>
                  Escanea desde WhatsApp → Dispositivos vinculados.
                </p>
              </div>
            ) : (
              <button
                className="app-btn app-btn-primary d-flex align-items-center gap-2"
                onClick={startConnect}
                disabled={connecting}
                style={{ background: BRAND_COLORS.gradients.primary }}
              >
                {connecting ? <RefreshCw size={16} className="spin" /> : <Smartphone size={16} />}
                {connecting ? "Generando QR..." : isConnected ? "Reconectar / cambiar número" : "Conectar número"}
              </button>
            )
          ) : pairingCode ? (
            <div className="text-center">
              <p className="sa-eyebrow mb-2">Código de vinculación</p>
              <div className="sa-pairing-code">{pairingCode}</div>
              <p className="small mt-2" style={{ color: BRAND_COLORS.text.muted, fontSize: "0.75rem" }}>
                En WhatsApp → Dispositivos vinculados → Vincular un dispositivo → <b>Vincular con número de teléfono</b>, escribe este código.
                Puede tardar unos segundos en quedar vinculado.
              </p>
            </div>
          ) : (
            <div className="d-flex flex-wrap gap-2 align-items-end">
              <div style={{ flex: "1 1 260px" }}>
                <CountryPhoneSelector
                  label="Número del WhatsApp de plataforma"
                  requeired={false}
                  selectedCountry={phoneCountry}
                  onCountryChange={setPhoneCountry}
                  phoneValue={phoneInput}
                  onPhoneChange={setPhoneInput}
                  phoneError={phoneError}
                  setPhoneError={setPhoneError}
                  placeholder="Sin la lada"
                  disabled={requestingCode}
                />
              </div>
              <button
                className="app-btn app-btn-primary d-flex align-items-center gap-2"
                onClick={requestPairingCode}
                disabled={requestingCode}
                style={{ background: BRAND_COLORS.gradients.primary }}
              >
                {requestingCode ? <RefreshCw size={16} className="spin" /> : <KeyRound size={16} />}
                {requestingCode ? "Generando código..." : "Obtener código"}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Número del superadmin para alertas */}
      <div className="app-card" style={{ backgroundColor: BRAND_COLORS.backgrounds.secondary, borderColor: BRAND_COLORS.borders.light }}>
        <div className="card-body" style={{ padding: "1.25rem" }}>
          <h3 className="h6 d-flex align-items-center gap-2 mb-1" style={{ color: BRAND_COLORS.primary.main, fontSize: "1rem" }}>
            <Bell size={18} /> Tu número para alertas
          </h3>
          <p className="small mb-3" style={{ color: BRAND_COLORS.text.muted, fontSize: "0.8rem" }}>
            A este número llegarán los avisos (nuevos registros, pagos, errores).
          </p>
          <div className="d-flex flex-wrap gap-2 align-items-end">
            <div style={{ flex: "1 1 260px" }}>
              <CountryPhoneSelector
                label="WhatsApp del superadmin"
                requeired={false}
                selectedCountry={adminCountry}
                onCountryChange={setAdminCountry}
                phoneValue={adminNumber}
                onPhoneChange={setAdminNumber}
                phoneError={adminNumberError}
                setPhoneError={setAdminNumberError}
                placeholder="Sin la lada"
                disabled={savingNumber}
              />
            </div>
            <button
              className="app-btn app-btn-primary d-flex align-items-center gap-2"
              onClick={saveAdminNumber}
              disabled={savingNumber}
              style={{ background: BRAND_COLORS.gradients.primary }}
            >
              <Save size={16} />
              {savingNumber ? "Guardando..." : "Guardar"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PlatformSettings;
